/**
 * §16 (Voice): feature-detect the Web Speech APIs. This is the ONLY place
 * that inspects `window` for speech support — `useSpeech`, `useDictation`,
 * and `MicButton` all go through this function rather than re-testing the
 * globals themselves, so there is exactly one source of truth for "does
 * voice work here."
 *
 * Re-detects on every call rather than caching a module-level constant.
 * jsdom (the test environment) has neither API, which is also the honest
 * behavior for a real unsupported browser: no throw, just `false`/`[]`.
 * `voices` may come back empty until the browser fires `voiceschanged` —
 * callers that need a voice (useSpeech) re-check at speak() time rather than
 * waiting on that event here.
 */
export type VoiceCapabilities = {
  canSpeak: boolean;
  canListen: boolean;
  voices: SpeechSynthesisVoice[];
};

function hasWindow(): boolean {
  return typeof window !== "undefined";
}

export function getVoiceCapabilities(): VoiceCapabilities {
  if (!hasWindow()) {
    return { canSpeak: false, canListen: false, voices: [] };
  }

  const canSpeak = "speechSynthesis" in window;
  const w = window as unknown as { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown };
  const canListen = Boolean(w.SpeechRecognition || w.webkitSpeechRecognition);

  let voices: SpeechSynthesisVoice[] = [];
  if (canSpeak) {
    try {
      voices = window.speechSynthesis.getVoices?.() ?? [];
    } catch {
      // A browser that advertises the API but throws on getVoices() is
      // still "supported" for our purposes — canSpeak stays true, we just
      // have no voice list to offer yet.
      voices = [];
    }
  }

  return { canSpeak, canListen, voices };
}
